import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  Select,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  RadioInputDiv,
  RadioWrapper,
  AdvancedOptionsButton,
} from '../../../styles/global'
import { Table, Th, Td, Tr, TableContainer, StatusMessage } from './styles'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

const PHI_OPTIONS = [5, 6.3, 8, 10, 12.5, 16, 20, 25]

export function ConcreteAnchorage() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(30)
  const [fyk, setFyk] = useState(500)
  const [b, setB] = useState('')
  const [d, setD] = useState('')
  const [vd, setVd] = useState('')
  const [asCalc, setAsCalc] = useState('')
  const [barsQuantity, setBarsQuantity] = useState('')
  const [phi, setPhi] = useState('16')
  const [bondCondition, setBondCondition] = useState('boa')
  const [hasBend, setHasBend] = useState(true)
  const [lbDisp, setLbDisp] = useState('')
  const [gammaC, setGammaC] = useState(1.4)
  const [gammaS, setGammaS] = useState(1.15)
  const [n1, setN1] = useState(2.25)
  const [n3, setN3] = useState(1)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-anchorage-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setB(entry.b)
      setD(entry.d)
      setVd(entry.vd)
      setAsCalc(entry.asCalc)
      setBarsQuantity(entry.barsQuantity)
      setPhi(entry.phi)
      setBondCondition(entry.bondCondition)
      setHasBend(entry.hasBend)
      setLbDisp(entry.lbDisp)
      setGammaC(entry.gammaC || 1.4)
      setGammaS(entry.gammaS || 1.15)
      setN1(entry.n1 || 2.25)
      setN3(entry.n3 || 1)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    if (name === 'barsQuantity' && !Number.isInteger(numValue)) {
      return 'Deve ser um número inteiro'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.b = validateField('b', b)
    newErrors.d = validateField('d', d)
    newErrors.vd = validateField('vd', vd)
    newErrors.asCalc = validateField('asCalc', asCalc)
    newErrors.barsQuantity = validateField('barsQuantity', barsQuantity)
    newErrors.lbDisp = validateField('lbDisp', lbDisp)
    newErrors.gammaC = validateField('gammaC', gammaC)
    newErrors.gammaS = validateField('gammaS', gammaS)
    newErrors.n1 = validateField('n1', n1)
    newErrors.n3 = validateField('n3', n3)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, b, d, vd, asCalc, barsQuantity, lbDisp, gammaC, gammaS, n1, n3, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        b,
        d,
        fck,
        fyk,
        vd,
        asCalc,
        barsQuantity,
        phi: Number(phi),
        bondCondition,
        hasBend,
        lbDisp,
        gammaC,
        gammaS,
        n1,
        n3,
      }

      setData(data)

      const response = await api.post('/concrete/anchorage', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        b,
        d,
        vd,
        asCalc,
        barsQuantity,
        phi,
        bondCondition,
        hasBend,
        lbDisp,
        gammaC,
        gammaS,
        n1,
        n3,
        data,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error(error.response?.data?.message || 'Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      b,
      d,
      vd,
      asCalc,
      barsQuantity,
      phi,
      bondCondition,
      hasBend,
      lbDisp,
      gammaC,
      gammaS,
      n1,
      n3,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Ancoragem.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setB(jsonData.b)
        setD(jsonData.d)
        setVd(jsonData.vd)
        setAsCalc(jsonData.asCalc)
        setBarsQuantity(jsonData.barsQuantity)
        setPhi(jsonData.phi)
        setBondCondition(jsonData.bondCondition)
        setHasBend(jsonData.hasBend)
        setLbDisp(jsonData.lbDisp)
        setGammaC(jsonData.gammaC || 1.4)
        setGammaS(jsonData.gammaS || 1.15)
        setN1(jsonData.n1 || 2.25)
        setN3(jsonData.n3 || 1)
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const phiCm = data.phi ? data.phi / 10 : 0
  const fyd = data.fyk && data.gammaS ? data.fyk / data.gammaS : 0
  const fctm = data.fck ? 0.3 * Math.pow(data.fck, 2 / 3) : 0
  const fctkInf = 0.7 * fctm
  const fctd = data.gammaC ? fctkInf / data.gammaC : 0
  const n2 = data.bondCondition === 'boa' ? 1 : 0.7
  const fbd = data.n1 && data.n3 ? data.n1 * n2 * data.n3 * fctd : 0
  const lbBasic = fbd ? (phiCm / 4) * (fyd / fbd) : 0
  const lbMin = result && result.lb ? Math.max(0.3 * result.lb, 10 * phiCm, 10) : 0

  const getDisplayRebarPairs = (rebarPairs) => {
    const oneRebarIndex = rebarPairs.findIndex((pair) => pair.quantity === 1)
    return oneRebarIndex === -1 ? rebarPairs : rebarPairs.slice(0, oneRebarIndex + 1)
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Ancoragem</span>
          </div>
          <h1>⚓ Ancoragem em Concreto Armado</h1>
          <p className="subtitle">
            Verificação da ancoragem de barras longitudinais no apoio e dimensionamento de grampo complementar
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Apoio e Grampo</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta verifica a ancoragem de barras longitudinais de vigas de concreto armado no apoio,
              considerando o comprimento disponível e a condição de aderência conforme a NBR 6118:2014.
            </p>
            <p>
              Quando o comprimento disponível não é suficiente, a calculadora dimensiona automaticamente um grampo
              complementar, sugerindo bitola e quantidade adequadas por ramo.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Ancoragem e Grampos</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Seção e Esforços</h3>
            <InputDiv>
              <span>Largura da viga (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura útil (cm)</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Cortante de Cálculo (kN)</span>
              <ValidatedInput
                name="vd"
                type="number"
                value={vd}
                onChange={(event) => setVd(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Armadura Longitudinal</h3>
            <InputDiv>
              <span>Área de aço calculada no vão (cm²)</span>
              <ValidatedInput
                name="asCalc"
                type="number"
                value={asCalc}
                onChange={(event) => setAsCalc(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Quantidade de barras adotadas</span>
              <ValidatedInput
                name="barsQuantity"
                type="number"
                step="1"
                value={barsQuantity}
                onChange={(event) => setBarsQuantity(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Bitola da armadura longitudinal (mm)</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={phi}
                  onChange={(event) => setPhi(event.target.value)}
                  disabled={isLoading}
                >
                  {PHI_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      Ø {option}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <h3>Ancoragem</h3>
            <RadioInputDiv>
              <span>Condição de Aderência</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    id="bond-boa"
                    name="bondCondition"
                    value="boa"
                    checked={bondCondition === 'boa'}
                    onChange={(e) => setBondCondition(e.target.value)}
                    disabled={isLoading}
                  />
                  <span>Boa aderência</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    id="bond-ma"
                    name="bondCondition"
                    value="ma aderencia"
                    checked={bondCondition === 'ma aderencia'}
                    onChange={(e) => setBondCondition(e.target.value)}
                    disabled={isLoading}
                  />
                  <span>Má aderência</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            <RadioInputDiv>
              <span>Possui dobra/gancho?</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    id="bend-yes"
                    name="hasBend"
                    checked={hasBend === true}
                    onChange={() => setHasBend(true)}
                    disabled={isLoading}
                  />
                  <span>Sim</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    id="bend-no"
                    name="hasBend"
                    checked={hasBend === false}
                    onChange={() => setHasBend(false)}
                    disabled={isLoading}
                  />
                  <span>Não</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            <InputDiv>
              <span>Comprimento disponível no apoio (cm)</span>
              <ValidatedInput
                name="lbDisp"
                type="number"
                value={lbDisp}
                onChange={(event) => setLbDisp(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <AdvancedOptionsButton
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              $isOpen={showAdvancedOptions}
            >
              <span className="arrow-icon">{showAdvancedOptions ? '▼' : '▶'}</span>
              <span className="button-text">{showAdvancedOptions ? 'Ocultar' : 'Mostrar'} Opções Avançadas</span>
            </AdvancedOptionsButton>
            {showAdvancedOptions && (
              <>
                <InputDiv>
                  <span>Coeficiente de segurança do concreto γc</span>
                  <ValidatedInput
                    name="gammaC"
                    type="number"
                    value={gammaC || ''}
                    onChange={(event) => setGammaC(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Coeficiente de segurança do aço γs</span>
                  <ValidatedInput
                    name="gammaS"
                    type="number"
                    value={gammaS || ''}
                    onChange={(event) => setGammaS(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Coeficiente de aderência η1</span>
                  <ValidatedInput
                    name="n1"
                    type="number"
                    value={n1 || ''}
                    onChange={(event) => setN1(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Coeficiente de aderência η3</span>
                  <ValidatedInput
                    name="n3"
                    type="number"
                    value={n3 || ''}
                    onChange={(event) => setN3(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Armadura principal</h3>
              <MathDiv>
                <BlockMath
                  math={`A_{s,ef}=${result.asEf} \\, \\text{cm}^2 \\quad A_{s,calc}=${result.asCalc} \\, \\text{cm}^2`}
                />
                {result.hasEnoughMainSteel ? (
                  <StatusMessage $status="success">✅ Armadura efetiva atende a área calculada</StatusMessage>
                ) : (
                  <StatusMessage $status="error">❌ Armadura efetiva não atende a área calculada</StatusMessage>
                )}
              </MathDiv>
              <h3>Ancoragem</h3>
              <MathDiv>
                <BlockMath
                  math={`l_{b,disp}=${result.lbDisp} \\, \\text{cm} \\quad l_{b,nec}=${roundToDecimals(result.lbNec, 2)} \\, \\text{cm}`}
                />
                {result.hasEnoughAnchorageLength && (
                  <>
                    <StatusMessage $status="success">✅ Ancoragem atende por comprimento</StatusMessage>
                    {result.support.shouldCheckSteel &&
                      (result.support.hasEnoughSteel ? (
                        <StatusMessage $status="success">
                          ✅ Armadura efetiva atende a área exigida no apoio
                        </StatusMessage>
                      ) : (
                        <StatusMessage $status="error">
                          ❌ Déficit de armadura no apoio: {result.support.steelDeficit} cm²
                        </StatusMessage>
                      ))}
                  </>
                )}
                {!result.hasEnoughAnchorageLength && result.clamps.canUseClamps && (
                  <StatusMessage $status="warning">⚠️ Comprimento insuficiente. Usar grampo complementar</StatusMessage>
                )}
                {!result.hasEnoughAnchorageLength && !result.clamps.canUseClamps && (
                  <StatusMessage $status="error">
                    ❌ Comprimento disponível inferior ao mínimo para usar grampo
                  </StatusMessage>
                )}
              </MathDiv>
              {!result.hasEnoughAnchorageLength && result.clamps.canUseClamps && (
                <>
                  <h3>Grampo complementar</h3>
                  <MathDiv>
                    <BlockMath
                      math={`F_{sd}=${result.clamps.fsd} \\, \\text{kN} \\quad A_{s1}=${result.clamps.as1} \\, \\text{cm}^2`}
                    />
                    <BlockMath
                      math={`A_{s1,ramo}=${result.clamps.as1PerBranch} \\, \\text{cm}^2 \\quad l_{b1}=${result.clamps.lb1} \\, \\text{cm}`}
                    />
                    <TableContainer>
                      <Table>
                        <thead>
                          <Tr>
                            <Th>Bitola</Th>
                            <Th>Quantidade de grampos</Th>
                          </Tr>
                        </thead>
                        <tbody>
                          {getDisplayRebarPairs(result.clamps.rebarPairs).map((pair, index) => (
                            <Tr key={index}>
                              <Td>Ø {pair.diameter} mm</Td>
                              <Td>{pair.quantity}</Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableContainer>
                  </MathDiv>
                </>
              )}
              {!result.hasEnoughAnchorageLength && !result.clamps.canUseClamps && (
                <MathDiv>
                  <BlockMath
                    math={`l_{b,disp}=${result.lbDisp} \\, \\text{cm} < s_{min}=${result.clamps.supportMinLength} \\, \\text{cm}`}
                  />
                  <p>{result.clamps.reason}</p>
                </MathDiv>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Resistência de aderência</h3>
              <MathDiv>
                <BlockMath
                  math={`f_{yd}=\\dfrac{f_{yk}}{\\gamma_s}=\\dfrac{${data.fyk}}{${data.gammaS}}=${roundToDecimals(fyd, 2)} \\, \\text{MPa}`}
                />
                <BlockMath
                  math={`f_{ctm}=0,3 \\cdot f_{ck}^{2/3}=0,3 \\cdot ${data.fck}^{2/3}=${roundToDecimals(fctm, 2)} \\, \\text{MPa}`}
                />
                <BlockMath math={`f_{ctk,inf}=0,7 \\cdot f_{ctm}=${roundToDecimals(fctkInf, 2)} \\, \\text{MPa}`} />
                <BlockMath
                  math={`f_{ctd}=\\dfrac{f_{ctk,inf}}{\\gamma_c}=\\dfrac{${roundToDecimals(fctkInf, 2)}}{${data.gammaC}}=${roundToDecimals(fctd, 2)} \\, \\text{MPa}`}
                />
                <BlockMath
                  math={`f_{bd}=\\eta_1 \\cdot \\eta_2 \\cdot \\eta_3 \\cdot f_{ctd}=${data.n1} \\cdot ${n2} \\cdot ${data.n3} \\cdot ${roundToDecimals(fctd, 2)}=${roundToDecimals(fbd, 2)} \\, \\text{MPa}`}
                />
              </MathDiv>
              <h3>Comprimento de ancoragem</h3>
              <MathDiv>
                <BlockMath
                  math={`l_{b,basic}=\\dfrac{\\phi}{4}\\cdot\\dfrac{f_{yd}}{f_{bd}}=\\dfrac{${phiCm}}{4}\\cdot\\dfrac{${roundToDecimals(fyd, 2)}}{${roundToDecimals(fbd, 2)}}=${roundToDecimals(lbBasic, 2)} \\, \\text{cm}`}
                />
                <BlockMath math={`l_b=\\max(l_{b,basic}, \\, 25 \\cdot \\phi)`} />
                <BlockMath math={`\\boxed{l_b=${roundToDecimals(result.lb, 2)} \\, \\text{cm}}`} />
                <BlockMath
                  math={`l_{b,min}=\\max(0,3 \\cdot l_b, \\, 10 \\cdot \\phi, \\, 10 \\, \\text{cm})=${roundToDecimals(lbMin, 2)} \\, \\text{cm}`}
                />
                {data.hasBend ? (
                  <BlockMath
                    math={`l_{b,nec}=\\max\\left(0,7 \\cdot l_b \\cdot \\dfrac{A_{s,calc}}{A_{s,ef}}, \\, l_{b,min}\\right)=\\max\\left(0,7 \\cdot ${roundToDecimals(result.lb, 2)} \\cdot \\dfrac{${result.asCalc}}{${result.asEf}}, \\, ${roundToDecimals(lbMin, 2)}\\right)`}
                  />
                ) : (
                  <BlockMath
                    math={`l_{b,nec}=\\max\\left(l_b \\cdot \\dfrac{A_{s,calc}}{A_{s,ef}}, \\, l_{b,min}\\right)=\\max\\left(${roundToDecimals(result.lb, 2)} \\cdot \\dfrac{${result.asCalc}}{${result.asEf}}, \\, ${roundToDecimals(lbMin, 2)}\\right)`}
                  />
                )}
                <BlockMath math={`\\boxed{l_{b,nec}=${roundToDecimals(result.lbNec, 2)} \\, \\text{cm}}`} />
                <BlockMath math={`l_{b,disp}=${result.lbDisp} \\, \\text{cm}`} />
                {result.hasEnoughAnchorageLength ? (
                  <BlockMath
                    math={`\\boxed{l_{b,disp} \\geq l_{b,nec} \\rightarrow \\text{Ancoragem atende por comprimento}}`}
                  />
                ) : (
                  <BlockMath math={`\\boxed{l_{b,disp} < l_{b,nec} \\rightarrow \\text{Comprimento insuficiente}}`} />
                )}
              </MathDiv>
              {result.hasEnoughAnchorageLength && result.support.shouldCheckSteel && (
                <>
                  <h3>Verificação do apoio</h3>
                  <MathDiv>
                    <BlockMath
                      math={`a_l=\\dfrac{d \\cdot V_d}{2 \\cdot (V_d - V_c)} \\quad \\left(0,5 \\cdot d \\leq a_l \\leq d\\right)`}
                    />
                    <BlockMath math={`a_l=${result.support.al} \\, \\text{cm}`} />
                    <BlockMath math={`R_{sd}=\\dfrac{a_l}{d} \\cdot V_d=${result.support.rsd} \\, \\text{kN}`} />
                    <BlockMath math={`A_{s,apoio}=\\dfrac{R_{sd}}{f_{yd}}=${result.support.asCalc} \\, \\text{cm}^2`} />
                    {result.support.hasEnoughSteel ? (
                      <StatusMessage $status="success">
                        ✅ Armadura efetiva ({roundToDecimals(result.asEf, 2)} cm²) atende a área exigida no apoio
                      </StatusMessage>
                    ) : (
                      <StatusMessage $status="error">
                        ❌ Déficit de armadura no apoio: {result.support.steelDeficit} cm²
                      </StatusMessage>
                    )}
                  </MathDiv>
                </>
              )}
              {!result.hasEnoughAnchorageLength && (
                <>
                  <h3>Mínimo para uso de grampo</h3>
                  <MathDiv>
                    <BlockMath
                      math={`s_{min}=\\max(R + 5,5 \\cdot \\phi, \\, 6 \\, \\text{cm})=${result.clamps.supportMinLength} \\, \\text{cm}`}
                    />
                  </MathDiv>
                  {result.clamps.canUseClamps ? (
                    <>
                      <h3>Dimensionamento do grampo</h3>
                      <MathDiv>
                        <BlockMath
                          math={`F_{sd}=R_{sd} \\cdot \\left(1 - \\dfrac{l_{b,disp}}{l_{b,nec}}\\right)=${result.clamps.fsd} \\, \\text{kN}`}
                        />
                        <BlockMath math={`A_{s1}=\\dfrac{F_{sd}}{f_{yd}}=${result.clamps.as1} \\, \\text{cm}^2`} />
                        <BlockMath
                          math={`A_{s1,ramo}=\\dfrac{A_{s1}}{2}=${result.clamps.as1PerBranch} \\, \\text{cm}^2`}
                        />
                        <BlockMath math={`l_{b1}=l_{b,basic}=${result.clamps.lb1} \\, \\text{cm}`} />
                        <TableContainer>
                          <Table>
                            <thead>
                              <Tr>
                                <Th>Bitola</Th>
                                <Th>Quantidade de grampos</Th>
                              </Tr>
                            </thead>
                            <tbody>
                              {getDisplayRebarPairs(result.clamps.rebarPairs).map((pair, index) => (
                                <Tr key={index}>
                                  <Td>Ø {pair.diameter} mm</Td>
                                  <Td>{pair.quantity}</Td>
                                </Tr>
                              ))}
                            </tbody>
                          </Table>
                        </TableContainer>
                      </MathDiv>
                    </>
                  ) : (
                    <MathDiv>
                      <BlockMath
                        math={`l_{b,disp}=${result.lbDisp} \\, \\text{cm} < s_{min}=${result.clamps.supportMinLength} \\, \\text{cm}`}
                      />
                      <p>{result.clamps.reason}</p>
                    </MathDiv>
                  )}
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
